// src/utils/fetch.ts
import { refreshTokenApi } from '../features/system/auth/api/authApi';
import { fmt } from './date';

/* ── JWT helpers (unchanged) ────────────────────────────────── */
function decodeJwtPayload(token: string): { exp?: number } | null {
  try {
    const [, payload] = token.split('.');
    const json = atob(
      payload.replace(/-/g, '+').replace(/_/g, '/')
        .padEnd(payload.length + (4 - (payload.length % 4)) % 4, '='),
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}
const isTokenExpired = (t: string) =>
  !decodeJwtPayload(t)?.exp || Date.now() / 1000 > decodeJwtPayload(t)!.exp!;

/* ── Types ──────────────────────────────────────────────────── */
export interface FetchOptions extends RequestInit {
  /** If true, do NOT add / refresh JWTs */
  skipAuth?: boolean;
}

/* ── ISO-timestamp pretty-printer (unchanged) ───────────────── */
function formatTimestamps(obj: any): any {
  if (obj === null || typeof obj !== 'object') {
    if (typeof obj === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(obj)) {
      return fmt(obj);
    }
    return obj;
  }
  if (Array.isArray(obj)) return obj.map(formatTimestamps);
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [k, formatTimestamps(v)]),
  );
}

/* ── Internal helpers ───────────────────────────────────────── */
async function ensureFreshJwt(): Promise<string> {
  const access = localStorage.getItem('jwt');
  const refresh = localStorage.getItem('refreshToken');
  if (!access || !refresh) throw new Error('Session expired. Please log in again.');

  if (!isTokenExpired(access)) return access;

  const { access_token: fresh } = await refreshTokenApi();
  localStorage.setItem('jwt', fresh);
  return fresh;
}

function withJwtHeaders(init: FetchOptions, jwt: string): FetchOptions {
  return {
    ...init,
    headers: {
      ...(init.headers as Record<string, string>),
      Authorization: `Bearer ${jwt}`, // local/dev
      'x-app-jwt': jwt,               // Cloud Run
    },
  };
}

/* ── Main helper ────────────────────────────────────────────── */
export async function fetchData(
  path: string,
  options: FetchOptions = {},
): Promise<any> {
  const method = (options.method || 'GET').toUpperCase();

  /*── 1. attach / refresh JWT if needed ──────────────────────*/
  let init: FetchOptions = { ...options };
  if (!options.skipAuth) {
    const jwt = await ensureFreshJwt();
    init = withJwtHeaders(init, jwt);
  }

  console.log(`👉 [fetchData] ${method} ${path}`, init);

  /*── 2. make the request (with one automatic 401 retry) ─────*/
  const doFetch = () => fetch(path, init).catch(err => {
    console.error(`❌ [fetchData] Network error for ${method} ${path}:`, err);
    throw new Error(`Network error: ${(err as Error).message}`);
  });

  let response = await doFetch();
  console.log(`👈 [fetchData] ${response.status} ${response.statusText} for ${path}`);

  if (response.status === 401 && !options.skipAuth) {
    // possible token revocation → try once more
    const freshJwt = await ensureFreshJwt();
    init = withJwtHeaders(init, freshJwt);
    response = await doFetch();
    console.log(`👈 [fetchData] Retry → ${response.status} ${response.statusText}`);
  }

  /*── 3. error handling ──────────────────────────────────────*/
  if (!response.ok) {
    const body = await response.text().catch(() => '<unreadable>');
    console.error(`❌ [fetchData] ${response.status} ${response.statusText} (${method} ${path})`);
    console.error('Body:', body);
    throw new Error(`Fetch failed: ${response.status} ${response.statusText} – ${body}`);
  }

  /*── 4. happy-path parsing ─────────────────────────────────*/
  // 204 / 205 → No Content
  if (response.status === 204 || response.status === 205) return;

  // Only parse JSON when the server says so
  const ctype = response.headers.get('content-type') ?? '';
  if (!ctype.startsWith('application/json')) return response.blob(); // caller decides

  let data: any;
  try {
    data = await response.json();
  } catch (e) {
    console.error(`❌ [fetchData] JSON parse error for ${path}:`, e);
    throw new Error(`Invalid JSON response: ${(e as Error).message}`);
  }

  console.log(`✅ [fetchData] Parsed JSON from ${path}:`, data);
  return formatTimestamps(data);
}
